import React, { useRef } from 'react';
import { SectionProps } from '../types/SectionProps';
import { Box, Container, Typography, Card, CardContent, Button, useTheme } from '@mui/material';
import { motion, useInView } from 'framer-motion';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';

const ServicesSection: React.FC<SectionProps> = ({ id }) => {
  const theme = useTheme();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const services = [
    {
      title: 'Conseil immobilier personnalisé',
      description: 'Acheter, vendre ou louer en toute sérénité, avec une stratégie adaptée à vos objectifs.',
      icon: <HomeWorkOutlinedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      image: 'https://images.pexels.com/photos/1546168/pexels-photo-1546168.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      title: 'Gestion de patrimoine immobilier',
      description: 'Structuration, valorisation et suivi de vos investissements pour construire un patrimoine pérenne.',
      icon: <AccountBalanceOutlinedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      image: 'https://images.pexels.com/photos/210607/pexels-photo-210607.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      title: 'Mise en relation professionnelle',
      description: 'Un réseau local qualifié pour vous faire gagner du temps et de la qualité.',
      icon: <HandshakeOutlinedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      image: 'https://images.pexels.com/photos/3184338/pexels-photo-3184338.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      title: 'Présence humaine et réactive',
      description: "Un interlocuteur unique, disponible et à l'écoute à chaque étape de votre projet.",
      icon: <SupportAgentOutlinedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      image: 'https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&w=800'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <Box
      id={id}
      ref={ref}
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: 'primary.main',
        color: 'white',
        scrollMarginTop: '64px', // Pour le défilement fluide
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography 
            variant="h2" 
            component="h2" 
            sx={{ 
              mb: 2,
              fontSize: { xs: '2rem', md: '2.5rem' },
              color: 'white',
              position: 'relative',
              display: 'inline-block'
            }}
          >
            Nos services
            <Box 
              sx={{ 
                position: 'absolute',
                bottom: -10,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '40px',
                height: '3px',
                bgcolor: 'error.main'
              }} 
            />
          </Typography>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              maxWidth: '700px', 
              mx: 'auto', 
              mt: 4,
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '1.1rem'
            }}
          >
            Des solutions claires, concrètes et personnalisées pour vous accompagner 
            dans tous vos projets immobiliers et patrimoniaux.
          </Typography>
        </Box>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 4 }}>
            {services.map((service, index) => (
              <Box key={index}>
                <motion.div variants={itemVariants} className="service-card">
                  <Card 
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 12px 20px rgba(0, 0, 0, 0.15)',
                      }
                    }}
                  >
                    <Box sx={{ 
                      height: '180px', 
                      position: 'relative',
                      bgcolor: 'rgba(248, 244, 241, 0.9)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {service.icon}
                    </Box>
                    <CardContent sx={{ flexGrow: 1, p: 3 }}>
                      <Typography 
                        gutterBottom 
                        variant="h5" 
                        component="h3" 
                        sx={{ 
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          mb: 2,
                          minHeight: '60px'
                        }}
                      >
                        {service.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {service.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Box>
            ))}
          </Box>
        </motion.div>

        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            href="#contact"
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              borderRadius: '2px',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
              '&:hover': {
                boxShadow: '0px 6px 15px rgba(0, 0, 0, 0.2)',
                bgcolor: '#c9ad6e',
              },
            }}
          >
            Prendre rendez-vous
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default ServicesSection;
